import { Image } from "@/components/common/Image";
import { ImageSize, dataURLtoFileList, imageSize } from "@/utils/helpers";
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormLabel,
  Icon,
  Link,
  Switch,
  Text,
  keyframes,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { File } from "react-feather";

const EXTRACT_FILE_NAME_REGEX = /.*[\/\\]/;

type Props = {
  name: string;
  value?: FileList | string;
  isDeleted?: boolean;
  onChange: (files?: FileList) => void;
  onDeletedChange?: (isDeleted: boolean) => void;
  isPictureInput?: boolean;
};

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, keyof Props> &
  Props;

const fadeAnimation = keyframes`
  0% {opacity: 0}
  50% {opacity: .5}
  100% {opacity: 0}
`;

const FileInput = React.forwardRef(
  (
    {
      name,
      value,
      isDeleted,
      onChange,
      onDeletedChange,
      isPictureInput,
      ...props
    }: InputProps,
    ref
  ): React.ReactElement => {
    const AvatarEdit = useMemo(
      () =>
        dynamic(() => import("react-avatar-edit"), {
          ssr: false,
        }),
      []
    );

    const fileInput = useRef<HTMLInputElement>();
    const [fileNames, setFileNames] = useState([]);
    const [previewImage, setPreviewImage] = useState<string>(null);
    const [previewImageDimensions, setPreviewImageDimensions] =
      useState<ImageSize>(null);
    const [canBeDeleted, setCanBeDeleted] = useState<boolean>(false);
    const [valueType, setValueType] = useState();
    const { t } = useTranslation("common");

    useEffect(() => {
      let newValueType;

      if (typeof value === "string") {
        newValueType = 1;
      } else if (value instanceof FileList && value.length > 0) {
        newValueType = 2;
      } else {
        newValueType = 3;
      }

      const setDimensions = async (image: string) =>
        setPreviewImageDimensions(await imageSize(image));

      if (newValueType === 1) {
        setFileNames([]);
        if (isPictureInput) {
          setPreviewImage(`/uploads/${value}`);
          setDimensions(`/uploads/${value}`);
        }
        setCanBeDeleted(true);
        onChange(null);
        // to block re-render for cropping tool
      } else if (newValueType === 2 && newValueType !== valueType) {
        if (isPictureInput) {
          setPreviewImage(URL.createObjectURL(value[0] as File));
          setDimensions(URL.createObjectURL(value[0] as File));
        }
        setCanBeDeleted(false);
        if (typeof onDeletedChange !== "undefined") onDeletedChange(false);
      } else if (newValueType === 3) {
        if (isPictureInput) {
          setPreviewImage(null);
          setDimensions(null);
        }
        setCanBeDeleted(false);
        if (typeof onDeletedChange !== "undefined") onDeletedChange(false);
      }

      setValueType(newValueType);
    }, [value]);

    const onPreviewCrop = useCallback((preview) => {
      onChange(
        dataURLtoFileList(
          preview,
          fileInput.current.files[0].name.replace(EXTRACT_FILE_NAME_REGEX, "")
        )
      );
    }, []);

    const onPreviewClose = useCallback(() => {
      fileInput.current.value = "";

      onChange(null);
    }, [fileInput]);

    const { publicRuntimeConfig } = getConfig();

    return (
      <>
        {isPictureInput && (
          <>
            <Collapse in={!!previewImage} animateOpacity>
              {previewImage &&
                (typeof value === "string" ? (
                  <Box
                    w={100}
                    h={100}
                    position="relative"
                    borderRadius="50%"
                    overflow="hidden"
                  >
                    <Image
                      src={previewImage}
                      alt=""
                      objectFit="cover"
                      unoptimized
                      quality={90}
                    />
                    {isDeleted && (
                      <Box
                        position="absolute"
                        inset="0"
                        bg="red.500"
                        animation={`${fadeAnimation} infinite 2s`}
                      />
                    )}
                  </Box>
                ) : (
                  <Box mb={2} overflow={{ base: "hidden", md: "visible" }}>
                    <Flex
                      direction="column"
                      flex={1}
                      alignItems="center"
                      justifyContent="center"
                      display={{ base: "none", md: "flex" }}
                    >
                      <AvatarEdit
                        src={previewImage}
                        width="100%"
                        height={400}
                        imageWidth={
                          previewImageDimensions?.width <
                          previewImageDimensions?.height
                            ? 400
                            : undefined
                        }
                        imageHeight={
                          previewImageDimensions?.height <
                          previewImageDimensions?.width
                            ? 400
                            : undefined
                        }
                        cropRadius={200}
                        minCropRadius={25}
                        exportAsSquare
                        exportSize={800}
                        onCrop={onPreviewCrop}
                        onClose={onPreviewClose}
                      />
                    </Flex>
                    <Flex
                      direction="column"
                      flex={1}
                      alignItems="center"
                      justifyContent="center"
                      display={{ base: "flex", md: "none" }}
                    >
                      <AvatarEdit
                        src={previewImage}
                        width={200}
                        height={200}
                        imageWidth={
                          previewImageDimensions?.width <
                          previewImageDimensions?.height
                            ? 400
                            : undefined
                        }
                        imageHeight={
                          previewImageDimensions?.height <
                          previewImageDimensions?.width
                            ? 400
                            : undefined
                        }
                        cropRadius={100}
                        minCropRadius={25}
                        exportAsSquare
                        exportSize={800}
                        onCrop={onPreviewCrop}
                        onClose={onPreviewClose}
                      />
                    </Flex>
                  </Box>
                ))}
            </Collapse>
          </>
        )}
        {!isPictureInput &&
          !props?.multiple &&
          fileNames.length === 0 &&
          typeof value === "string" && (
            <Link
              target="_blank"
              href={`${publicRuntimeConfig.basePath}/uploads/${value}`}
              rel="noopener noreferrer"
            >
              <Flex maxW="100%" mb={1} alignItems="center">
                <Icon as={File} mr={1} />
                <Text noOfLines={1}>{t("preview")}</Text>
              </Flex>
            </Link>
          )}
        <input
          {...props}
          hidden
          ref={fileInput}
          type="file"
          onChange={(e) => {
            const files = [...e.target.files];

            setFileNames([
              ...files.map((file: File) =>
                file.name.replace(EXTRACT_FILE_NAME_REGEX, "")
              ),
            ]);

            onChange(e.target.files.length > 0 ? e.target.files : null);

            if (isPictureInput) {
              setValueType(null);
            }
          }}
          {...(isPictureInput
            ? {
                accept: "image/jpeg,image/png",
              }
            : {})}
        />
        {onDeletedChange && canBeDeleted && (
          <Box my={2}>
            <Switch
              id={`deletePicture-${name}`}
              isChecked={isDeleted}
              onChange={(event) => onDeletedChange(event.target.checked)}
            />
            <FormLabel
              pb="0"
              pl={2}
              verticalAlign="middle"
              htmlFor={`deletePicture-${name}`}
              cursor="pointer"
            >
              {t("delete")}
            </FormLabel>
          </Box>
        )}
        <Flex
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          maxW="100%"
        >
          <Button
            onClick={() => fileInput?.current?.click()}
            size="sm"
            flexShrink={0}
            flex="none"
            ref={ref as React.RefObject<HTMLButtonElement>}
          >
            {props?.multiple ? t("chooseFiles") : t("chooseFile")}
          </Button>
          <Text
            ml={{ base: 0, md: 3 }}
            mt={{ base: 3, md: 0 }}
            fontWeight="bold"
            isTruncated
            maxW="100%"
          >
            {fileNames.join(", ")}
          </Text>
        </Flex>
      </>
    );
  }
);

FileInput.displayName = "FileInput";

export default FileInput;
